import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMercenary } from '../../contexts/MercenaryContext';
import { useSocket } from '../../contexts/SocketContext';
import GuildEmblem from './GuildEmblem';
import GuildBattleLineup from './GuildBattleLineup';
import BuildingDetailModal from './BuildingDetailModal';
import ToastContainer from '../Shared/ToastContainer';
import Modal from '../Shared/Modal';
import { useToast } from '../../hooks/useToast';
import axios from 'axios';
import styles from './GuildDashboard.module.css';
import Tooltip from '../Pages/ItemTooltip';
import { getItemImageSrc, handleImgError } from '../../utils/itemUtils';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

interface Guild {
  id: number;
  name: string;
  tag: string;
  level: number;
  experience: number;
  experienceToNext: number;
  memberCount: number;
  maxMembers: number;
  emblem: {
    animalIcon?: string;
    animalHue?: number;
    animalSaturation?: number;
    animalBrightness?: number;
    shieldType?: string;
    // Legacy fields
    backgroundColor?: string;
    iconType?: string;
    iconColor?: string;
    borderType?: string;
    borderColor?: string;
  };
  buildings: {
    trainingGroundLevel: number;
    libraryLevel: number;
    infirmaryLevel: number;
    warehouseLevel: number;
    sleepingHallLevel: number;
  };
  vault: {
    silver: number;
    capacity: number;
    itemCapacity: number;
    silverCapacity: number;
  };
}

interface GuildMember {
  id: number;
  username: string;
  displayName: string | null;
  memberRole: string;
  joinedAt: string;
  contributionPoints: number;
  lastActive: string;
  dailySilverLimit: number;
  dailyItemLimit: number;
  todaysSilverWithdrawn: number;
  todaysItemsWithdrawn: number;
  unlimitedWithdrawals: boolean;
  xpContributed: number;
  leaguePointsContributed: number;
  silverDonated: number;
  roundsDonated: number;
  materialsDonated: Record<string, number>;
}

interface GuildApplication {
  id: number;
  guildId: number;
  userId: number;
  message: string | null;
  status: string;
  createdAt: string;
  applicant: {
    id: number;
    username: string;
    mercenaries: Array<{
      id: number;
      name: string;
      level: number;
      race: string;
    }>;
  };
}

const VAULT_SUBCATEGORIES: Record<string, string[]> = {
  Weapon: ['Axe', 'Sword', 'Mace', 'Dagger', 'Staff', 'Chain'],
  Armor: ['Head', 'Shoulder', 'Torso', 'Hand', 'Leg', 'Feet'],
  Accessory: ['Amulet', 'Armring', 'Cloak', 'Neck', 'Ring'],
  Knowledge: ['Tome', 'Scroll', 'Book'],
  Rune: ['green', 'orange', 'blue', 'red', 'aqua', 'yellow', 'gray', 'purple'],
};

const GuildDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mercenaryId } = useMercenary();
  const { socket } = useSocket();
  const [guild, setGuild] = useState<Guild | null>(null);
  const [members, setMembers] = useState<GuildMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [userRole, setUserRole] = useState('Member');
  const [currentSeason, setCurrentSeason] = useState<any>(null);
  const [leagueStanding, setLeagueStanding] = useState<any>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [applications, setApplications] = useState<GuildApplication[]>([]);
  const [applicationCount, setApplicationCount] = useState<number>(0);

  // Warehouse state
  const [vaultItems, setVaultItems] = useState<Array<{ id: number; itemId: number; name: string; quantity: number; depositedBy: number; item: any }>>([]);
  const [inventory, setInventory] = useState<Array<{ id: number; itemId: number; name: string; quantity: number; item: any }>>([]);
  const [vaultAction, setVaultAction] = useState<'deposit' | 'withdraw'>('deposit');
  const [vaultSilver, setVaultSilver] = useState<string>('');
  const [selectedVaultItem, setSelectedVaultItem] = useState<number | null>(null);
  const [vaultItemQty, setVaultItemQty] = useState<string>('');
  const [memberSortKey, setMemberSortKey] = useState<string>('rank');
  const [memberSortDir, setMemberSortDir] = useState<'asc' | 'desc'>('asc');
  const [pendingLimits, setPendingLimits] = useState<Record<number, { silver: string; items: string }>>({});
  const [vaultLoading, setVaultLoading] = useState(false);
  const [myMember, setMyMember] = useState<GuildMember | null>(null);
  const [vaultSubTab, setVaultSubTab] = useState<'actions' | 'logs'>('actions');
  // Vault filter state
  const [vaultSearch, setVaultSearch] = useState('');
  const [vaultFilterCat, setVaultFilterCat] = useState<string | null>(null);
  const [vaultFilterSub, setVaultFilterSub] = useState<string | null>(null);
  const [vaultLogs, setVaultLogs] = useState<Array<{
    id: number;
    action: 'deposit' | 'withdraw' | 'league_reward';
    silver: number | null;
    items: string | null;
    createdAt: string;
    Actor: { username: string; };
  }>>([]);
  const [logFilterUser, setLogFilterUser] = useState('');
  const [logFilterType, setLogFilterType] = useState<'all' | 'deposit' | 'withdraw' | 'league_reward'>('all');
  const [logFilterContent, setLogFilterContent] = useState<'all' | 'silver' | 'items'>('all');
  const [logFilterItem, setLogFilterItem] = useState('');
  const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
  const { toasts, showToast, hideToast } = useToast();
  // Dedup online notifications: track recently notified userIds to prevent duplicates
  const recentOnlineNotifs = useRef<Set<number>>(new Set());
  
  // Modal state for custom popups
  const [modalState, setModalState] = useState<{
    show: boolean;
    title: string;
    message: React.ReactNode;
    type: 'alert' | 'confirm';
    onConfirm?: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    type: 'alert'
  });

  // No-guild state: browse & apply
  const [guildList, setGuildList] = useState<Array<{
    id: number; name: string; tag: string; level: number; totalMembers: number;
  }>>([]);
  const [guildListLoading, setGuildListLoading] = useState(false);
  const [appliedGuilds, setAppliedGuilds] = useState<Set<number>>(new Set());
  const [applyingTo, setApplyingTo] = useState<number | null>(null);
  const [applyMessage, setApplyMessage] = useState('');
  const [showApplyForm, setShowApplyForm] = useState<number | null>(null);
  const [claimingLibraryXp, setClaimingLibraryXp] = useState(false);
  const [libraryVisited, setLibraryVisited] = useState(false);
  const [libraryResetAt, setLibraryResetAt] = useState<Date | null>(null);
  const [libraryCountdown, setLibraryCountdown] = useState('');

  useEffect(() => {
    fetchGuildData();
    fetchApplicationCount();
  }, []);

  useEffect(() => {
    if (mercenaryId) {
      fetchLibraryStatus();
    }
  }, [mercenaryId]);

  useEffect(() => {
    if (!libraryVisited || !libraryResetAt) {
      setLibraryCountdown('');
      return;
    }
    const tick = () => {
      const diff = libraryResetAt.getTime() - Date.now();
      if (diff <= 0) {
        setLibraryVisited(false);
        setLibraryCountdown('');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLibraryCountdown(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [libraryVisited, libraryResetAt]);

  // Listen for guild member online notifications
  useEffect(() => {
    if (!socket) return;
    const handler = (data: { userId: number; username: string; displayName: string }) => {
      setOnlineUserIds(prev => new Set(prev).add(data.userId));
      // Deduplicate: only show toast once per user per 10 seconds
      if (!recentOnlineNotifs.current.has(data.userId)) {
        recentOnlineNotifs.current.add(data.userId);
        showToast(`${data.displayName} is now online`, 'info');
        setTimeout(() => recentOnlineNotifs.current.delete(data.userId), 10000);
      }
    };
    socket.on('guildMemberOnline', handler);
    return () => { socket.off('guildMemberOnline', handler); };
  }, [socket, showToast]);

  useEffect(() => {
    if (guild?.id) {
      fetchSeasonData(guild.id);
    }
  }, [guild?.id]);

  const fetchSeasonData = async (guildId: number) => {
    try {
      // Fetch current season (response is wrapped: { season: {...} })
      const seasonRes = await fetch('/api/seasons/current', { credentials: 'include' });
      if (seasonRes.ok) {
        const data = await seasonRes.json();
        const season = data.season;
        if (!season) return; // no active season
        setCurrentSeason(season);

        // Fetch the active guild league, then get standings for this guild
        const leagueRes = await fetch('/api/guild-league/current', { credentials: 'include' });
        if (leagueRes.ok) {
          const league = await leagueRes.json();
          const standingsRes = await fetch(`/api/guild-league/${league.id}/standings`, {
            credentials: 'include'
          });
          if (standingsRes.ok) {
            const standings = await standingsRes.json();
            // Add rank position to each standing entry
            const rankedStandings = standings.map((s: any, idx: number) => ({ ...s, position: idx + 1 }));
            const myStanding = rankedStandings.find((s: any) => s.guildId === guildId);
            setLeagueStanding(myStanding);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching season data:', error);
    }
  };

  const fetchApplicationCount = async () => {
    try {
      const response = await axios.get('/api/guild/applications/count', { withCredentials: true });
      setApplicationCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching application count:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get('/api/guild/applications', { withCredentials: true });
      setApplications(response.data.applications || []);
    } catch (error: any) {
      if (error.response?.status === 403 || error.response?.status === 404) {
        // Not authorized or not in guild
        setApplications([]);
      } else {
        console.error('Error fetching applications:', error);
      }
    }
  };

  const fetchGuildData = async () => {
    try {
      const response = await fetch('/api/guild/my-guild', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setGuild(data.guild);
        setMembers(data.members || []);
        setUserRole(data.userRole || 'Member');
        // Track the current user's own member record for limit display
        if (user && data.members) {
          const me = data.members.find((m: GuildMember) => m.id === user.id);
          setMyMember(me || null);
        }
        // Seed onlineUserIds: anyone active within the last 5 minutes
        const fiveMinAgo = Date.now() - 5 * 60 * 1000;
        const onlineNow = new Set<number>(
          (data.members || [])
            .filter((m: GuildMember) => new Date(m.lastActive).getTime() > fiveMinAgo)
            .map((m: GuildMember) => m.id)
        );
        setOnlineUserIds(onlineNow);
      } else if (response.status === 404) {
        // User is not in a guild - load the public guild list
        setGuild(null);
        fetchGuildList();
      } else {
        setError('Failed to load guild data');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchGuildList = async () => {
    setGuildListLoading(true);
    try {
      const res = await axios.get('/api/guild/ranking?page=1', { withCredentials: true });
      setGuildList(res.data.guilds || []);

      // Pre-populate which guilds the user has already applied to
      const guilds: Array<{ id: number }> = res.data.guilds || [];
      const checks = await Promise.allSettled(
        guilds.map(g =>
          axios.get(`/api/guild/applications/check/${g.id}`, { withCredentials: true })
            .then(r => ({ id: g.id, hasPending: r.data.hasPending }))
        )
      );
      const alreadyApplied = new Set<number>();
      checks.forEach(result => {
        if (result.status === 'fulfilled' && result.value.hasPending) {
          alreadyApplied.add(result.value.id);
        }
      });
      setAppliedGuilds(alreadyApplied);
    } catch (error) {
      console.error('Error fetching guild list:', error);
    } finally {
      setGuildListLoading(false);
    }
  };

  const handleApplyToGuild = async (guildId: number) => {
    setApplyingTo(guildId);
    try {
      await axios.post(`/api/guild/${guildId}/apply`, { message: applyMessage || null }, { withCredentials: true });
      setAppliedGuilds(prev => new Set(prev).add(guildId));
      setShowApplyForm(null);
      setApplyMessage('');
      showToast('Application submitted! Guild leaders will review it soon.', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to submit application', 'error');
    } finally {
      setApplyingTo(null);
    }
  };

  const handleLeaveGuild = async () => {
    const confirmMsg = userRole === 'Guild Master'
      ? 'You are the Guild Master. Leaving will transfer ownership to the highest-ranked longest-serving member. Are you sure?'
      : 'Are you sure you want to leave the guild?';
    setModalState({
      show: true,
      title: 'Leave Guild',
      message: confirmMsg,
      type: 'confirm',
      onConfirm: async () => {
        setModalState({ ...modalState, show: false });
        try {
          await axios.post('/api/guild/leave', {}, { withCredentials: true });
          showToast('You have left the guild', 'info');
          setGuild(null);
          setMembers([]);
          setUserRole('Member');
          fetchGuildList();
        } catch (err: any) {
          showToast(err.response?.data?.message || 'Failed to leave guild', 'error');
        }
      }
    });
  };

  const handleKickMember = async (targetUserId: number, username: string, displayName?: string | null) => {
    const displayedName = displayName || username;
    setModalState({
      show: true,
      title: 'Remove Member',
      message: `Remove ${displayedName} from the guild?`,
      type: 'confirm',
      onConfirm: async () => {
        setModalState(prev => ({ ...prev, show: false }));
        try {
          await axios.post('/api/guild/kick', { targetUserId }, { withCredentials: true });
          showToast(`${displayedName} has been removed from the guild`, 'success');
          fetchGuildData();
        } catch (err: any) {
          showToast(err.response?.data?.message || 'Failed to remove member', 'error');
        }
      }
    });
  };

  const handleTransferOwnership = async (targetUserId: number, username: string, displayName?: string | null) => {
    const displayedName = displayName || username;
    setModalState({
      show: true,
      title: 'Transfer Guild Master',
      message: `Transfer Guild Master role to ${displayedName}? You will be demoted to Officer.`,
      type: 'confirm',
      onConfirm: async () => {
        setModalState(prev => ({ ...prev, show: false }));
        try {
          await axios.post('/api/guild/transfer-ownership', { targetUserId }, { withCredentials: true });
          showToast(`Guild Master role transferred to ${displayedName}`, 'success');
          fetchGuildData();
        } catch (err: any) {
          showToast(err.response?.data?.message || 'Failed to transfer ownership', 'error');
        }
      }
    });
  };

  const fetchLibraryStatus = async () => {
    if (!mercenaryId) return;
    try {
      const res = await axios.get(`/api/guild/library/status?mercenaryId=${mercenaryId}`, { withCredentials: true });
      setLibraryVisited(res.data.hasVisited);
      setLibraryResetAt(res.data.hasVisited ? new Date(res.data.resetAt) : null);
    } catch {
      // ignore, button will work normally
    }
  };

  const handleClaimLibraryXp = async () => {
    setClaimingLibraryXp(true);
    try {
      const response = await axios.post('/api/guild/library/visit', { mercenaryId }, { withCredentials: true });
      showToast(response.data.message, 'success');
      const now = new Date();
      const resetAt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
      setLibraryVisited(true);
      setLibraryResetAt(resetAt);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to visit library', 'error');
    } finally {
      setClaimingLibraryXp(false);
    }
  };

  const fetchVaultItems = useCallback(async () => {
    try {
      const res = await axios.get('/api/guild/vault', { withCredentials: true });
      // Backend returns { vault: {...}, items: [...] }
      // Equippable items have one GuildVaultItem row per instance, aggregate by itemId
      // so the UI shows a single entry with total quantity instead of duplicate rows.
      const rawItems = res.data.items || [];
      const aggregated = new Map<number, any>();
      for (const vi of rawItems) {
        const existing = aggregated.get(vi.itemId);
        if (existing) {
          existing.quantity += vi.quantity;
        } else {
          aggregated.set(vi.itemId, {
            id: vi.id,
            itemId: vi.itemId,
            name: vi.Item?.name || vi.item?.name || `Item #${vi.itemId}`,
            quantity: vi.quantity,
            depositedBy: vi.depositedBy,
            item: vi.Item || vi.item || {}
          });
        }
      }
      setVaultItems(Array.from(aggregated.values()));
    } catch (error) {
      console.error('Error fetching guild vault items:', error);
    }
  }, []);

  const fetchMercenaryInventory = useCallback(async () => {
    try {
      // Reuse the vault inventory endpoint - it returns active mercenary's items
      const res = await axios.get('/api/vault/inventory', { withCredentials: true });
      const items = res.data || [];
      setInventory(items.map((inv: any) => ({
        id: inv.id,
        itemId: inv.id,
        name: inv.name,
        quantity: inv.quantity,
        item: inv
      })));
    } catch (error) {
      console.error('Error fetching mercenary inventory:', error);
    }
  }, []);

  const fetchVaultLogs = useCallback(async () => {
    try {
      const res = await axios.get('/api/guild/vault/logs', { withCredentials: true });
      setVaultLogs(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching guild vault logs:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'vault') {
      fetchVaultItems();
      fetchMercenaryInventory();
      fetchVaultLogs();
    }
  }, [activeTab, fetchVaultItems, fetchMercenaryInventory, fetchVaultLogs]);

  const renderEmblem = (emblem: Guild['emblem'], size: 'small' | 'medium' | 'large' = 'medium') => {
    // Support new emblem system
    if (emblem.animalIcon !== undefined && emblem.animalHue !== undefined && emblem.animalSaturation !== undefined && emblem.animalBrightness !== undefined) {
      return (
        <GuildEmblem 
          animalIcon={emblem.animalIcon}
          animalHue={emblem.animalHue}
          animalSaturation={emblem.animalSaturation}
          animalBrightness={emblem.animalBrightness}
          shieldType={emblem.shieldType || 'Shield.png'}
          size={size}
        />
      );
    }
    
    // Fallback to legacy emblem rendering
    return (
      <div 
        className={styles.emblem}
        style={{
          backgroundColor: emblem.backgroundColor,
          borderColor: emblem.borderColor
        }}
      >
        <div 
          className={`${styles.emblemIcon} ${styles[`icon-${emblem.iconType}`]} ${styles[`border-${emblem.borderType}`]}`}
          style={{ color: emblem.iconColor }}
        >
          {emblem.iconType?.charAt(0).toUpperCase()}
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (!guild) return null;

    return (
      <div className={styles.overview}>
        <div className={styles.guildHeader}>
          {renderEmblem(guild.emblem, 'large')}
          <div className={styles.guildInfo}>
            <h2>[{guild.tag}] {guild.name}</h2>
            <div className={styles.guildStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Level:</span>
                <span className={styles.statValue}>{guild.level}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Members:</span>
                <span className={styles.statValue}>{guild.memberCount}/{guild.maxMembers}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Experience:</span>
                <span className={styles.statValue}>
                  {guild.experience.toLocaleString('en-GB')} / {guild.experienceToNext.toLocaleString('en-GB')}
                </span>
              </div>
            </div>
            <div className={styles.experienceBar}>
              <div 
                className={styles.experienceProgress}
                style={{ width: `${(guild.experience / guild.experienceToNext) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className={styles.buildingsGrid}>
          <div 
            className={`${styles.building} ${styles.clickable}`}
            onClick={() => setSelectedBuilding({
              name: 'Training Ground',
              level: guild.buildings.trainingGroundLevel,
              description: 'The training ground is where guild members hone their combat skills together.',
              benefit: 'Increases combat effectiveness in guild league battles'
            })}
          >
            <h4>Training Ground</h4>
            <p>Level {guild.buildings.trainingGroundLevel}</p>
            <small>Increases combat effectiveness in league battles</small>
          </div>
          <div 
            className={`${styles.building} ${styles.clickable}`}
            onClick={() => setSelectedBuilding({
              name: 'Infirmary',
              level: guild.buildings.infirmaryLevel,
              description: 'The infirmary provides medical care to wounded mercenaries, speeding their recovery.',
              benefit: 'Reduces recovery time for wounded mercenaries'
            })}
          >
            <h4>Infirmary</h4>
            <p>Level {guild.buildings.infirmaryLevel}</p>
            <small>Reduces recovery time for wounded mercenaries</small>
          </div>
          <div 
            className={`${styles.building} ${styles.clickable}`}
            onClick={() => setSelectedBuilding({
              name: 'Library',
              level: guild.buildings.libraryLevel,
              description: 'The library is a repository of knowledge where members can study and gain experience.',
              benefit: 'Allows members to visit daily and gain experience'
            })}
          >
            <h4>Library</h4>
            <p>Level {guild.buildings.libraryLevel}</p>
            <small>Allow members to visit and gain experience</small>
          </div>
          <div 
            className={`${styles.building} ${styles.clickable}`}
            onClick={() => setSelectedBuilding({
              name: 'Warehouse',
              level: guild.buildings.warehouseLevel,
              description: 'The warehouse expands the guild warehouse storage, allowing more items and silver to be stored.',
              benefit: 'Increases warehouse capacity'
            })}
          >
            <h4>Warehouse</h4>
            <p>Level {guild.buildings.warehouseLevel}</p>
            <small>Increases warehouse capacity ({guild.vault.itemCapacity} item stacks, {guild.vault.silverCapacity.toLocaleString()} silver)</small>
          </div>
          <div 
            className={`${styles.building} ${styles.clickable}`}
            onClick={() => setSelectedBuilding({
              name: 'Sleeping Hall',
              level: guild.buildings.sleepingHallLevel,
              description: 'The sleeping hall provides accommodation for guild members.',
              benefit: 'Increases maximum member limit'
            })}
          >
            <h4>Sleeping Hall</h4>
            <p>Level {guild.buildings.sleepingHallLevel}</p>
            <small>Increases member limit, +2 members per level</small>
          </div>
        </div>

        {guild.buildings.warehouseLevel > 0 && (
          <div className={styles.vaultInfo}>
            <h3>Guild Warehouse</h3>
            <div className={styles.vaultStats}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Silver:</span>
                <span className={styles.statValue}>{guild.vault.silver.toLocaleString()} / {guild.vault.silverCapacity.toLocaleString()}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Item Stacks:</span>
                <span className={styles.statValue}>{guild.vault.capacity} max</span>
              </div>
            </div>
          </div>
        )}

        {guild.buildings.libraryLevel > 0 && (
          <div className={styles.libraryClaimSection}>
            <h3>Guild Library</h3>
            <p>Study at the library to gain experience for your mercenary.</p>
            <button
              className={styles.libraryClaimBtn}
              onClick={handleClaimLibraryXp}
              disabled={claimingLibraryXp || libraryVisited}
            >
              {claimingLibraryXp ? 'Visiting...' : libraryVisited ? `Revisit in ${libraryCountdown}` : 'Visit Library'}
            </button>
          </div>
        )}

        <div className={styles.leaveGuildSection}>
          <button className={styles.leaveGuildBtn} onClick={handleLeaveGuild}>
            Leave Guild
          </button>
        </div>
      </div>
    );
  };

  const renderMembers = () => {
    const sortableColumns: { key: string; label: string }[] = [
      { key: 'rank',                   label: 'Rank' },
      { key: 'username',               label: 'Name' },
      { key: 'xpContributed',          label: 'XP Contributed' },
      { key: 'leaguePointsContributed',label: 'Season Pts' },
      { key: 'silverDonated',          label: 'Silver Donated' },
      { key: 'roundsDonated',          label: 'Rounds Donated' },
      { key: 'materialsDonated',       label: 'Materials Donated' },
      { key: 'joinedAt',               label: 'Joined' },
      { key: 'lastActive',             label: 'Last Active' },
    ];

    const roleOrder: Record<string, number> = {
      'Guild Master': 0, 'Guild Leader': 1, 'Officer': 2, 'General': 3,
      'Veteran': 4, 'Member': 5, 'Recruit': 6
    };

    const handleSort = (key: string) => {
      if (memberSortKey === key) {
        setMemberSortDir(d => d === 'asc' ? 'desc' : 'asc');
      } else {
        setMemberSortKey(key);
        setMemberSortDir('asc');
      }
    };

    const sorted = [...members].sort((a, b) => {
      let av: any, bv: any;
      if (memberSortKey === 'rank') {
        av = roleOrder[a.memberRole] ?? 99;
        bv = roleOrder[b.memberRole] ?? 99;
      } else if (memberSortKey === 'materialsDonated') {
        av = Object.values(a.materialsDonated || {}).reduce((s: number, n: any) => s + (n || 0), 0);
        bv = Object.values(b.materialsDonated || {}).reduce((s: number, n: any) => s + (n || 0), 0);
      } else if (memberSortKey === 'joinedAt' || memberSortKey === 'lastActive') {
        av = new Date(a[memberSortKey as keyof GuildMember] as string).getTime();
        bv = new Date(b[memberSortKey as keyof GuildMember] as string).getTime();
      } else if (memberSortKey === 'username') {
        av = (a.displayName || a.username).toLowerCase();
        bv = (b.displayName || b.username).toLowerCase();
      } else {
        av = (a as any)[memberSortKey] ?? 0;
        bv = (b as any)[memberSortKey] ?? 0;
      }
      if (av < bv) return memberSortDir === 'asc' ? -1 : 1;
      if (av > bv) return memberSortDir === 'asc' ? 1 : -1;
      return (a.displayName || a.username).localeCompare(b.displayName || b.username);
    });

    const arrow = (key: string) => memberSortKey === key ? (memberSortDir === 'asc' ? ' ▲' : ' ▼') : '';

    return (
      <div className={styles.members}>
        <h3>Guild Members ({members.length})</h3>
        <div className={styles.membersTableWrapper}>
          <table className={styles.membersTable}>
            <thead>
              <tr>
                {sortableColumns.map(col => (
                  <th
                    key={col.key}
                    className={styles.sortableHeader}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}{arrow(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(member => {
                const totalMats = Object.values(member.materialsDonated || {}).reduce(
                  (s: number, n: any) => s + (n || 0), 0
                );
                const matDetail = Object.entries(member.materialsDonated || {})
                  .filter(([, v]) => (v as number) > 0)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(', ');
                return (
                  <tr key={member.id}>
                    <td>
                      <span className={`${styles.role} ${styles[member.memberRole.toLowerCase().replace(' ', '')]}`}>
                        {member.memberRole}
                      </span>
                    </td>
                    <td>{member.displayName || member.username}</td>
                    <td>{member.xpContributed || 0}</td>
                    <td>{member.leaguePointsContributed || 0}</td>
                    <td>{member.silverDonated || 0}</td>
                    <td>{member.roundsDonated || 0}</td>
                    <td title={matDetail || '-'}>{totalMats || 0}</td>
                    <td>{formatDate(member.joinedAt)}</td>
                    <td>
                      {onlineUserIds.has(member.id) ? (
                        <span className={styles.onlineBadge}>● Online</span>
                      ) : (
                        formatDate(member.lastActive)
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderVault = () => {
    if (!guild) return null;
    
    // Check if vault building is built
    const vaultNotBuilt = guild.buildings.warehouseLevel < 1;
    
    if (vaultNotBuilt) {
      return (
        <div className={styles.vault}>
          <h3>Guild Warehouse</h3>
          <div className={styles.vaultLocked}>
            <p>The warehouse must be built first before it can be used.</p>
            <p>Upgrade the Warehouse building to level 1 to unlock the warehouse.</p>
            <button 
              className={styles.buildButton}
              onClick={() => setActiveTab('overview')}
            >
              Go to Buildings
            </button>
          </div>
        </div>
      );
    }

    const isLeaderOrOfficer = ['Guild Master', 'Guild Leader', 'Officer'].includes(userRole);
    const silverLimit = myMember?.dailySilverLimit ?? 0;
    const itemLimit = myMember?.dailyItemLimit ?? 0;
    const silverUsed = myMember?.todaysSilverWithdrawn ?? 0;
    const itemsUsed = myMember?.todaysItemsWithdrawn ?? 0;
    const unlimited = myMember?.unlimitedWithdrawals ?? isLeaderOrOfficer;

    const currentItems = vaultAction === 'deposit' ? inventory : vaultItems;

    const getMaxSilver = () => {
      if (vaultAction === 'deposit') {
        // Use mercenary's silver (from context or fetch), guild vault remaining capacity
        const remaining = guild.vault.silverCapacity - guild.vault.silver;
        return remaining; // The actual mercenary silver limit is checked server-side
      } else {
        // Withdraw: limited by vault balance and daily limit remaining
        let max = guild.vault.silver;
        if (!unlimited) {
          const remainingLimit = silverLimit - silverUsed;
          max = Math.min(max, Math.max(0, remainingLimit));
        }
        return max;
      }
    };

    const handleItemAction = async (itemId: number, qty: number) => {
      if (!mercenaryId) {
        showToast('No active mercenary found.', 'error');
        return;
      }
      if (qty <= 0) return;

      setVaultLoading(true);
      try {
        const payload: any = { mercenaryId, items: [{ itemId, quantity: qty }] };
        const response = await axios.post(`/api/guild/vault/${vaultAction}`, payload, { withCredentials: true });
        showToast(`${vaultAction === 'deposit' ? 'Deposited' : 'Withdrew'} successfully!`, 'success');
        setSelectedVaultItem(null);
        setVaultItemQty('');
        fetchVaultItems();
        fetchMercenaryInventory();
        fetchVaultLogs();
        if (vaultAction === 'withdraw' && response.data && myMember) {
          setMyMember({
            ...myMember,
            todaysSilverWithdrawn: response.data.todaysSilverWithdrawn ?? myMember.todaysSilverWithdrawn,
            todaysItemsWithdrawn: response.data.todaysItemsWithdrawn ?? myMember.todaysItemsWithdrawn
          });
        }
        fetchGuildData();
      } catch (err: any) {
        showToast(err.response?.data?.message || `Failed to ${vaultAction}.`, 'error');
      } finally {
        setVaultLoading(false);
      }
    };

    const handleSilverAction = async () => {
      if (!mercenaryId) {
        showToast('No active mercenary found.', 'error');
        return;
      }
      const silverAmount = parseInt(vaultSilver) || 0;
      if (silverAmount <= 0) {
        showToast('Enter a silver amount.', 'error');
        return;
      }

      setVaultLoading(true);
      try {
        const payload: any = { mercenaryId, silver: silverAmount };
        const response = await axios.post(`/api/guild/vault/${vaultAction}`, payload, { withCredentials: true });
        showToast(`${vaultAction === 'deposit' ? 'Deposited' : 'Withdrew'} ${silverAmount.toLocaleString()} silver!`, 'success');
        setVaultSilver('');
        fetchVaultItems();
        fetchMercenaryInventory();
        fetchVaultLogs();
        if (vaultAction === 'withdraw' && response.data && myMember) {
          setMyMember({
            ...myMember,
            todaysSilverWithdrawn: response.data.todaysSilverWithdrawn ?? myMember.todaysSilverWithdrawn,
            todaysItemsWithdrawn: response.data.todaysItemsWithdrawn ?? myMember.todaysItemsWithdrawn
          });
        }
        fetchGuildData();
      } catch (err: any) {
        showToast(err.response?.data?.message || `Failed to ${vaultAction}.`, 'error');
      } finally {
        setVaultLoading(false);
      }
    };

    const selectedItem = currentItems.find(i => i.itemId === selectedVaultItem);
    const selectedMax = selectedItem?.quantity || 1;
    const currentQty = parseInt(vaultItemQty) || selectedMax;

    // ── Vault filtering helpers ──────────────────────────────────────────────
    const filterVaultItems = <T extends { name: string; item: any }>(items: T[]): T[] => {
      return items.filter(entry => {
        const itm = entry.item;
        if (vaultSearch && !entry.name.toLowerCase().includes(vaultSearch.toLowerCase())) return false;
        if (vaultFilterCat) {
          if (!itm || itm.itemCategory !== vaultFilterCat) return false;
          if (vaultFilterSub) {
            const sub = vaultFilterSub;
            if (vaultFilterCat === 'Weapon' && itm.weaponType !== sub) return false;
            else if (vaultFilterCat === 'Armor' && itm.armorType !== sub) return false;
            else if (vaultFilterCat === 'Accessory' && itm.accessoryType !== sub) return false;
            else if (vaultFilterCat === 'Rune' && itm.runeColor !== sub) return false;
            else if (vaultFilterCat === 'Knowledge' && itm.slot !== sub) return false;
          }
        }
        return true;
      });
    };

    const filteredCurrentItems = filterVaultItems(currentItems);
    const filteredVaultContents = filterVaultItems(vaultItems);
    const allItemsForFilter = vaultAction === 'deposit' ? [...inventory, ...vaultItems] : vaultItems;
    const availableCategories = Array.from(
      new Set(allItemsForFilter.map(i => i.item?.itemCategory).filter(Boolean))
    ) as string[];
    const subcategories = vaultFilterCat ? (VAULT_SUBCATEGORIES[vaultFilterCat] || []) : [];
    const hasFilter = !!(vaultSearch || vaultFilterCat);

    return (
      <div className={styles.vault}>
        <h3>Guild Warehouse</h3>

        {/* Sub-tabs: Actions / Logs */}
        <div className={styles.vaultSubTabs}>
          <button
            className={`${styles.vaultSubTabBtn} ${vaultSubTab === 'actions' ? styles.active : ''}`}
            onClick={() => setVaultSubTab('actions')}
          >
            Deposit / Withdraw
          </button>
          <button
            className={`${styles.vaultSubTabBtn} ${vaultSubTab === 'logs' ? styles.active : ''}`}
            onClick={() => { setVaultSubTab('logs'); fetchVaultLogs(); }}
          >
            Transaction Logs
          </button>
        </div>

        {vaultSubTab === 'logs' ? (
          <div className={styles.vaultLogs}>
            {/* Filter bar */}
            <div className={styles.logFilters}>
              <input
                type="text"
                className={styles.logFilterInput}
                placeholder="Filter by player..."
                value={logFilterUser}
                onChange={e => setLogFilterUser(e.target.value)}
              />
              <select
                className={styles.logFilterSelect}
                style={{ width: 'auto' }}
                value={logFilterType}
                  onChange={e => setLogFilterType(e.target.value as 'all' | 'deposit' | 'withdraw' | 'league_reward')}
              >
                <option value="all">All actions</option>
                <option value="deposit">Deposits only</option>
                <option value="withdraw">Withdrawals only</option>
                  <option value="league_reward">League rewards only</option>
              </select>
              <select
                className={styles.logFilterSelect}
                style={{ width: 'auto' }}
                value={logFilterContent}
                onChange={e => setLogFilterContent(e.target.value as 'all' | 'silver' | 'items')}
              >
                <option value="all">Silver &amp; Items</option>
                <option value="silver">Silver only</option>
                <option value="items">Items only</option>
              </select>
              <input
                type="text"
                className={styles.logFilterInput}
                placeholder="Search item name..."
                value={logFilterItem}
                onChange={e => setLogFilterItem(e.target.value)}
              />
              {(logFilterUser || logFilterType !== 'all' || logFilterContent !== 'all' || logFilterItem) && (
                <button
                  className={styles.logFilterClear}
                  onClick={() => { setLogFilterUser(''); setLogFilterType('all'); setLogFilterContent('all'); setLogFilterItem(''); }}
                >
                  Clear
                </button>
              )}
            </div>
            {(() => {
              const filtered = vaultLogs.filter(log => {
                const actorName = log.action === 'league_reward' ? 'System' : (log.Actor?.username ?? '');
                const logItems: Array<{ itemId: number; itemName: string; quantity: number }> =
                  log.items ? JSON.parse(log.items) : [];
                if (logFilterUser && !actorName.toLowerCase().includes(logFilterUser.toLowerCase())) return false;
                if (logFilterType !== 'all' && log.action !== logFilterType) return false;
                if (logFilterContent === 'silver' && log.silver == null) return false;
                if (logFilterContent === 'items' && logItems.length === 0) return false;
                if (logFilterItem && !logItems.some(i => i.itemName.toLowerCase().includes(logFilterItem.toLowerCase()))) return false;
                return true;
              });
              if (filtered.length === 0) return <p className={styles.vaultNote}>{vaultLogs.length === 0 ? 'No transactions recorded yet.' : 'No transactions match the filters.'}</p>;
              return (
                <table className={styles.vaultLogsTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Player</th>
                      <th>Action</th>
                      <th>Silver</th>
                      <th>Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(log => {
                      const actorName = log.action === 'league_reward' ? 'System' : (log.Actor?.username ?? '-');
                      const actionLabel = log.action === 'deposit'
                        ? 'Deposit'
                        : log.action === 'withdraw'
                          ? 'Withdraw'
                          : 'Guild League Reward';
                      const logItems: Array<{ itemId: number; itemName: string; quantity: number }> =
                        log.items ? JSON.parse(log.items) : [];
                      return (
                        <tr
                          key={log.id}
                          className={
                            log.action === 'withdraw'
                              ? styles.logWithdraw
                              : log.action === 'league_reward'
                                ? styles.logReward
                                : styles.logDeposit
                          }
                        >
                          <td>{formatDateTime(log.createdAt)}</td>
                          <td>{actorName}</td>
                          <td className={styles.logAction}>{actionLabel}</td>
                          <td>{log.silver != null ? log.silver.toLocaleString() : '-'}</td>
                          <td>
                            {logItems.length > 0
                              ? logItems.map(i => `${i.itemName} x${i.quantity}`).join(', ')
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        ) : (
          <>
        {/* Vault stats */}
        <div className={styles.vaultStats}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Silver:</span>
            <span className={styles.statValue}>
              {guild.vault.silver.toLocaleString()} / {guild.vault.silverCapacity.toLocaleString()}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Items:</span>
            <span className={styles.statValue}>
              {vaultItems.reduce((sum, item) => {
                const cat = item.item?.itemCategory;
                const isEquippable = ['Weapon', 'Shield', 'Armor', 'Accessory'].includes(cat);
                return sum + (isEquippable ? item.quantity : 1);
              }, 0)} / {guild.vault.itemCapacity} stacks
            </span>
          </div>
          {!unlimited && (
            <>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Silver withdrawn today:</span>
                <span className={styles.statValue}>{silverUsed.toLocaleString()} / {silverLimit.toLocaleString()}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Items withdrawn today:</span>
                <span className={styles.statValue}>{itemsUsed} / {itemLimit}</span>
              </div>
            </>
          )}
          {unlimited && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>Withdrawal limits:</span>
              <span className={styles.statValue}>Unlimited</span>
            </div>
          )}
        </div>

        {/* Action toggle */}
        <div className={styles.vaultActionToggle}>
          <button
            className={`${styles.vaultToggleBtn} ${vaultAction === 'deposit' ? styles.active : ''}`}
            onClick={() => { setVaultAction('deposit'); setSelectedVaultItem(null); setVaultSilver(''); setVaultItemQty(''); setVaultSearch(''); setVaultFilterCat(null); setVaultFilterSub(null); }}
          >
            Deposit
          </button>
          <button
            className={`${styles.vaultToggleBtn} ${vaultAction === 'withdraw' ? styles.active : ''}`}
            onClick={() => { setVaultAction('withdraw'); setSelectedVaultItem(null); setVaultSilver(''); setVaultItemQty(''); setVaultSearch(''); setVaultFilterCat(null); setVaultFilterSub(null); }}
          >
            Withdraw
          </button>
        </div>

        {/* Silver section */}
        <div className={styles.vaultSilverRow}>
          <label>Silver:</label>
          <div className={styles.vaultSilverControls}>
            <input
              type="number"
              value={vaultSilver}
              placeholder="0"
              min="0"
              onChange={(e) => setVaultSilver(e.target.value)}
              className={styles.vaultInput}
            />
            <button
              className={styles.vaultMaxBtn}
              onClick={() => setVaultSilver(String(getMaxSilver()))}
            >
              Max
            </button>
            <button
              className={styles.vaultActionBtn}
              onClick={handleSilverAction}
              disabled={vaultLoading || !vaultSilver || parseInt(vaultSilver) <= 0}
            >
              {vaultLoading ? '...' : vaultAction === 'deposit' ? 'Deposit' : 'Withdraw'}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        {(currentItems.length > 0 || vaultItems.length > 0) && (
          <div className={styles.vaultFilterBar}>
            <input
              type="text"
              className={styles.vaultSearchInput}
              placeholder="Search items by name..."
              value={vaultSearch}
              onChange={e => { setVaultSearch(e.target.value); setVaultFilterCat(null); setVaultFilterSub(null); }}
            />
            {availableCategories.length > 0 && (
              <div className={styles.vaultFilterCategories}>
                {availableCategories.map(cat => (
                  <button
                    key={cat}
                    className={`${styles.vaultFilterCatBtn} ${vaultFilterCat === cat ? styles.vaultFilterCatBtnActive : ''}`}
                    onClick={() => {
                      if (vaultFilterCat === cat) { setVaultFilterCat(null); setVaultFilterSub(null); }
                      else { setVaultFilterCat(cat); setVaultFilterSub(null); }
                      setVaultSearch('');
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
            {vaultFilterCat && subcategories.length > 0 && (
              <div className={styles.vaultFilterSubcategories}>
                {subcategories.map(sub => (
                  <button
                    key={sub}
                    className={`${styles.vaultFilterSubBtn} ${vaultFilterSub === sub ? styles.vaultFilterSubBtnActive : ''}`}
                    onClick={() => setVaultFilterSub(vaultFilterSub === sub ? null : sub)}
                  >
                    {sub.charAt(0).toUpperCase() + sub.slice(1)}
                  </button>
                ))}
              </div>
            )}
            {hasFilter && (
              <button
                className={styles.vaultFilterClear}
                onClick={() => { setVaultSearch(''); setVaultFilterCat(null); setVaultFilterSub(null); }}
              >
                ✕ Clear filters
              </button>
            )}
          </div>
        )}

        {/* Items section */}
        <div className={styles.vaultItemsSection}>
          <h4>{vaultAction === 'deposit' ? 'Your Inventory' : 'Vault Items'}</h4>
          {filteredCurrentItems.length === 0 ? (
            <p className={styles.vaultNote}>
              {hasFilter
                ? 'No items match the current filters.'
                : vaultAction === 'deposit' ? 'Your inventory is empty.' : 'The vault has no items.'}
            </p>
          ) : (
            <div className={styles.vaultItemsGrid}>
              {filteredCurrentItems.map((item) => {
                const isSelected = selectedVaultItem === item.itemId;
                return (
                  <div key={item.itemId} className={`${styles.vaultItemCard} ${isSelected ? styles.vaultItemCardSelected : ''}`}>
                    <Tooltip itemId={item.itemId} item={item.item} style={{ display: 'block', flex: 1, minWidth: 0 }}>
                      <div
                        className={styles.vaultItemCardInner}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedVaultItem(null);
                            setVaultItemQty('');
                          } else {
                            setSelectedVaultItem(item.itemId);
                            setVaultItemQty(String(item.quantity));
                          }
                        }}
                      >
                        <img
                          src={getItemImageSrc(item.item)}
                          alt={item.name}
                          className={styles.vaultItemIcon}
                          onError={handleImgError}
                        />
                        <span className={styles.vaultItemCardName}>{item.name}</span>
                        <span className={styles.vaultItemCardQty}>x{item.quantity}</span>
                      </div>
                    </Tooltip>
                    {isSelected && (
                      <div className={styles.vaultItemActions}>
                        <button
                          className={styles.vaultQtyBtn}
                          onClick={() => setVaultItemQty(String(Math.max(1, currentQty - 1)))}
                          disabled={currentQty <= 1}
                        >−</button>
                        <input
                          type="number"
                          className={styles.vaultQtyDisplay}
                          value={currentQty}
                          min={1}
                          max={selectedMax}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            if (isNaN(v) || v < 1) setVaultItemQty('1');
                            else if (v > selectedMax) setVaultItemQty(String(selectedMax));
                            else setVaultItemQty(String(v));
                          }}
                        />
                        <button
                          className={styles.vaultQtyBtn}
                          onClick={() => setVaultItemQty(String(Math.min(selectedMax, currentQty + 1)))}
                          disabled={currentQty >= selectedMax}
                        >+</button>
                        <button
                          className={styles.vaultItemConfirmBtn}
                          onClick={() => handleItemAction(item.itemId, currentQty)}
                          disabled={vaultLoading}
                        >
                          {vaultLoading ? '...' : vaultAction === 'deposit' ? 'Deposit' : 'Withdraw'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Vault contents (always visible when depositing) */}
        {vaultAction === 'deposit' && (
          vaultItems.length > 0 ? (
            <div className={styles.vaultContents}>
              <h4>Vault Contents{hasFilter && filteredVaultContents.length !== vaultItems.length ? ` (${filteredVaultContents.length}/${vaultItems.length})` : ''}</h4>
              {filteredVaultContents.length === 0 ? (
                <p className={styles.vaultNote}>No vault items match the current filters.</p>
              ) : (
                <div className={styles.vaultItemsList}>
                  {filteredVaultContents.map((item) => (
                    <Tooltip key={item.itemId} itemId={item.itemId} item={item.item}
                      style={{ width: '100%', display: 'block' }}>
                      <div className={styles.vaultItem}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <img src={getItemImageSrc(item.item)} alt={item.name}
                            className={styles.vaultItemIcon}
                            onError={handleImgError} />
                          <span className={styles.vaultItemName}>{item.name}</span>
                        </div>
                        <span className={styles.vaultItemQty}>x{item.quantity}</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className={styles.vaultNote}>The vault is empty.</p>
          )
        )}
          </>
        )}
      </div>
    );
  };

  const renderLeague = () => {
    const isLeader = userRole === 'Guild Master' || userRole === 'Guild Leader';
    const isOfficer = userRole === 'Officer';
    
    return (
      <div className={styles.league}>
        <h3>Guild League</h3>
        {!currentSeason ? (
          <p>No active season</p>
        ) : (
          <>
            <div className={styles.leagueInfo}>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Current Season:</span>
                <span className={styles.statValue}>{currentSeason.name || 'Season ' + currentSeason.seasonNumber}</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>League Position:</span>
                <span className={styles.statValue}>
                  {leagueStanding?.position || '-'}
                </span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statLabel}>Points:</span>
                <span className={styles.statValue}>{leagueStanding?.points || 0}</span>
              </div>
              {leagueStanding && (
                <>
                  <div className={styles.stat}>
                    <span className={styles.statLabel}>W/L:</span>
                    <span className={styles.statValue}>
                      {leagueStanding.wins}/{leagueStanding.losses}
                    </span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statLabel}>Kills:</span>
                    <span className={styles.statValue}>{leagueStanding.kills || 0}</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Battle Lineup Section */}
            {guild && (
              <div className={styles.battleLineupSection}>
                <GuildBattleLineup guildId={guild.id} userId={user?.id ?? 0} isLeader={isLeader} isOfficer={isOfficer} />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const updateMemberRole = async (memberId: number, newRole: string) => {
    try {
      const response = await fetch('/api/guild/update-member-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ memberId, role: newRole })
      });

      if (response.ok) {
        fetchGuildData(); // Refresh data
      } else {
        setError('Failed to update member role');
      }
    } catch (error) {
      setError('Network error occurred');
    }
  };

  const updateMemberLimits = async (memberId: number, silverLimit: number, itemLimit: number, unlimitedWithdrawals?: boolean) => {
    try {
      const payload: any = { memberId, silverLimit, itemLimit };
      if (typeof unlimitedWithdrawals === 'boolean') {
        payload.unlimitedWithdrawals = unlimitedWithdrawals;
      }
      
      const response = await fetch('/api/guild/update-member-limits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setPendingLimits(prev => {
          const next = { ...prev };
          delete next[memberId];
          return next;
        });
        fetchGuildData(); // Refresh data
      } else {
        setError('Failed to update member limits');
      }
    } catch (error) {
      setError('Network error occurred');
    }
  };

  const renderManagement = () => {
    const isLeader = userRole === 'Guild Master' || userRole === 'Guild Leader';
    const isOfficer = userRole === 'Officer';
    
    if (!isLeader && !isOfficer) {
      return <div>Access denied. Only Guild Leaders and Officers can access management.</div>;
    }

    return (
      <div className={styles.management}>
        <h3>Guild Management</h3>

        <div className={styles.managementSection}>
          <h4>Member Permissions</h4>
          <div className={styles.memberManagement}>
            {members.map(member => {
              const isTargetLeader = ['Guild Master', 'Guild Leader'].includes(member.memberRole);
              const canChangeRole = isLeader && !isTargetLeader;
              const canSetLimits = isLeader || (isOfficer && !['Guild Leader', 'Officer'].includes(member.memberRole));
              
              return (
                <div key={member.id} className={styles.memberManagementCard}>
                  <span className={styles.memberNameCell}>{member.displayName || member.username}</span>

                  <div className={styles.limitColumn}>
                    <label className={styles.limitLabel}>Role</label>
                    {canChangeRole ? (
                      <select
                        value={member.memberRole}
                        onChange={(e) => updateMemberRole(member.id, e.target.value)}
                        className={styles.roleSelect}
                      >
                        <option value="Officer">Officer</option>
                        <option value="General">General</option>
                        <option value="Veteran">Veteran</option>
                        <option value="Member">Member</option>
                        <option value="Recruit">Recruit</option>
                      </select>
                    ) : (
                      <span className={`${styles.role} ${styles[member.memberRole.toLowerCase().replace(' ', '')]}`}>
                        {member.memberRole}
                      </span>
                    )}
                  </div>

                  {canSetLimits && !isTargetLeader && (
                    <>
                      {!member.unlimitedWithdrawals && (
                        <div className={styles.limitColumn}>
                          <label className={styles.limitLabel}>Silver/day</label>
                          <input
                            type="number"
                            value={pendingLimits[member.id]?.silver ?? (member.dailySilverLimit || '')}
                            placeholder="0"
                            onChange={(e) => setPendingLimits(prev => ({
                              ...prev,
                              [member.id]: { silver: e.target.value, items: prev[member.id]?.items ?? String(member.dailyItemLimit) }
                            }))}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              updateMemberLimits(member.id, val, member.dailyItemLimit, member.unlimitedWithdrawals);
                            }}
                            className={styles.limitInputSmall}
                            min="0"
                          />
                          <span className={styles.limitUsage}>Used: {member.todaysSilverWithdrawn}</span>
                        </div>
                      )}

                      {!member.unlimitedWithdrawals && (
                        <div className={styles.limitColumn}>
                          <label className={styles.limitLabel}>Items/day</label>
                          <input
                            type="number"
                            value={pendingLimits[member.id]?.items ?? (member.dailyItemLimit || '')}
                            placeholder="0"
                            onChange={(e) => setPendingLimits(prev => ({
                              ...prev,
                              [member.id]: { silver: prev[member.id]?.silver ?? String(member.dailySilverLimit), items: e.target.value }
                            }))}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              updateMemberLimits(member.id, member.dailySilverLimit, val, member.unlimitedWithdrawals);
                            }}
                            className={styles.limitInputSmall}
                            min="0"
                          />
                          <span className={styles.limitUsage}>Used: {member.todaysItemsWithdrawn}</span>
                        </div>
                      )}

                      <div className={styles.limitColumn}>
                        <label className={styles.limitLabel}>&nbsp;</label>
                        <label className={styles.unlimitedControl}>
                          <input
                            type="checkbox"
                            checked={member.unlimitedWithdrawals || false}
                            onChange={(e) => updateMemberLimits(member.id, member.dailySilverLimit, member.dailyItemLimit, e.target.checked)}
                          />
                          <span>Unlimited</span>
                        </label>
                      </div>
                    </>
                  )}

                  {isTargetLeader && (
                    <span className={styles.leaderNote}>Guild Leader has no limits</span>
                  )}

                  {/* Kick button - cannot kick self or GM */}
                  {user && member.id !== user.id && !isTargetLeader && (isLeader || isOfficer) && (
                    <button
                      className={styles.kickBtn}
                      onClick={() => handleKickMember(member.id, member.username, member.displayName)}
                    >
                      Kick
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Transfer Ownership - Guild Master only */}
        {userRole === 'Guild Master' && (() => {
          const transferCandidates = members.filter(
            m => user && m.id !== user.id && m.memberRole !== 'Guild Master'
          );
          return (
            <div className={styles.managementSection}>
              <h4>Transfer Guild Master</h4>
              <p className={styles.transferNote}>
                Pass the Guild Master role to another member. You will be demoted to Officer.
              </p>
              {transferCandidates.length === 0 ? (
                <p className={styles.transferNote}>No other members to transfer to.</p>
              ) : (
                <div className={styles.transferOwnershipRow}>
                  <select
                    className={styles.transferSelect}
                    defaultValue=""
                    id="transfer-ownership-select"
                  >
                    <option value="" disabled>Select a member...</option>
                    {transferCandidates.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.displayName || m.username} ({m.memberRole})
                      </option>
                    ))}
                  </select>
                  <button
                    className={styles.transferOwnershipBtn}
                    onClick={() => {
                      const select = document.getElementById('transfer-ownership-select') as HTMLSelectElement;
                      const targetId = parseInt(select.value);
                      const target = transferCandidates.find(m => m.id === targetId);
                      if (!target) return;
                      handleTransferOwnership(target.id, target.username, target.displayName);
                    }}
                  >
                    Transfer
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderApplications = () => {
    const isLeader = userRole === 'Guild Master' || userRole === 'Guild Leader';
    const isOfficer = userRole === 'Officer';

    if (!isLeader && !isOfficer) {
      return <div className={styles.noAccess}>Only Guild Leaders and Officers can view applications.</div>;
    }

    const handleAcceptApplication = async (applicationId: number) => {
      setModalState({
        show: true,
        title: 'Accept Application',
        message: 'Accept this application and add the user to the guild?',
        type: 'confirm',
        onConfirm: async () => {
          setModalState(prev => ({ ...prev, show: false }));
          try {
            await axios.post(`/api/guild/applications/${applicationId}/accept`, {}, { withCredentials: true });
            fetchApplications();
            fetchApplicationCount();
            fetchGuildData();
            setModalState({
              show: true,
              title: 'Success',
              message: 'Application accepted successfully!',
              type: 'alert'
            });
          } catch (error: any) {
            setModalState({
              show: true,
              title: 'Error',
              message: error.response?.data?.message || 'Failed to accept application',
              type: 'alert'
            });
          }
        }
      });
    };

    const handleDeclineApplication = async (applicationId: number) => {
      setModalState({
        show: true,
        title: 'Decline Application',
        message: 'Decline this application?',
        type: 'confirm',
        onConfirm: async () => {
          setModalState(prev => ({ ...prev, show: false }));
          try {
            await axios.post(`/api/guild/applications/${applicationId}/decline`, {}, { withCredentials: true });
            fetchApplications();
            fetchApplicationCount();
            setModalState({
              show: true,
              title: 'Success',
              message: 'Application declined successfully!',
              type: 'alert'
            });
          } catch (error: any) {
            setModalState({
              show: true,
              title: 'Error',
              message: error.response?.data?.message || 'Failed to decline application',
              type: 'alert'
            });
          }
        }
      });
    };

    if (!applications || applications.length === 0) {
      return (
        <div className={styles.applications}>
          <h3>Guild Applications</h3>
          <p className={styles.noApplications}>No pending applications</p>
        </div>
      );
    }

    return (
      <div className={styles.applications}>
        <h3>Guild Applications ({applications.length})</h3>
        <div className={styles.applicationsList}>
          {applications.map(application => (
            <div key={application.id} className={styles.applicationCard}>
              <div className={styles.applicationHeader}>
                <h4>{application.applicant.username}</h4>
                <span className={styles.applicationDate}>
                  {formatDate(application.createdAt)}
                </span>
              </div>

              {application.applicant.mercenaries && application.applicant.mercenaries.length > 0 && (
                <div className={styles.mercenariesInfo}>
                  <h5>Mercenaries:</h5>
                  {application.applicant.mercenaries.map(merc => (
                    <div key={merc.id} className={styles.mercenaryInfo}>
                      <span className={styles.mercName}>{merc.name}</span>
                      <span className={styles.mercDetails}>
                        Level {merc.level} {merc.race}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {application.message && (
                <div className={styles.applicationMessage}>
                  <p><strong>Message:</strong> {application.message}</p>
                </div>
              )}

              <div className={styles.applicationActions}>
                <button
                  className="level-up-button"
                  onClick={() => handleAcceptApplication(application.id)}
                >
                  Accept
                </button>
                <button
                  className="danger-button"
                  onClick={() => handleDeclineApplication(application.id)}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading guild data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!guild) {
    return (
      <div className={styles.container}>
        <ToastContainer toasts={toasts} onClose={hideToast} />
        <div className={styles.noGuild}>
          <h2>You are not in a guild</h2>
          <p>Create your own guild or apply to join an existing one.</p>
          <button
            className={styles.createButton}
            onClick={() => navigate('/guild/create')}
          >
            Create Guild
          </button>
        </div>

        <div className={styles.guildBrowse}>
          <h3 className={styles.guildBrowseTitle}>Available Guilds</h3>
          {guildListLoading ? (
            <p className={styles.loading}>Loading guilds...</p>
          ) : guildList.length === 0 ? (
            <p className={styles.noGuildsText}>No guilds found.</p>
          ) : (
            <div className={styles.guildBrowseList}>
              {guildList.map(g => {
                const applied = appliedGuilds.has(g.id);
                const isOpen = showApplyForm === g.id;
                return (
                  <div key={g.id} className={styles.guildBrowseCard}>
                    <div className={styles.guildBrowseInfo}>
                      <span className={styles.guildBrowseName}>
                        {g.name} <span className={styles.guildBrowseTag}>[{g.tag}]</span>
                      </span>
                      <span className={styles.guildBrowseMeta}>
                        Lv. {g.level} &nbsp;·&nbsp; {g.totalMembers} members
                      </span>
                    </div>
                    <div className={styles.guildBrowseActions}>
                      {applied ? (
                        <span className={styles.appliedBadge}>⏳ Pending Application</span>
                      ) : isOpen ? (
                        <div className={styles.inlineApplyForm}>
                          <textarea
                            className={styles.applyTextarea}
                            placeholder="Optional message to guild leaders..."
                            value={applyMessage}
                            onChange={e => setApplyMessage(e.target.value)}
                            rows={2}
                            maxLength={500}
                          />
                          <div className={styles.inlineApplyActions}>
                            <button
                              className={styles.submitApplyBtn}
                              onClick={() => handleApplyToGuild(g.id)}
                              disabled={applyingTo === g.id}
                            >
                              {applyingTo === g.id ? 'Sending...' : 'Submit'}
                            </button>
                            <button
                              className={styles.cancelApplyBtn}
                              onClick={() => { setShowApplyForm(null); setApplyMessage(''); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          className={styles.applyBtn}
                          onClick={() => setShowApplyForm(g.id)}
                        >
                          Apply
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ToastContainer toasts={toasts} onClose={hideToast} />
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'members' ? styles.active : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Members
        </button>
        {guild && guild.buildings.warehouseLevel >= 1 && (
          <button 
            className={`${styles.tab} ${activeTab === 'vault' ? styles.active : ''}`}
            onClick={() => setActiveTab('vault')}
            data-tab="vault"
          >
            Warehouse
          </button>
        )}
        <button
          className={`${styles.tab} ${activeTab === 'league' ? styles.active : ''}`}
          onClick={() => setActiveTab('league')}
        >
          League
        </button>
        {(userRole === 'Guild Master' || userRole === 'Guild Leader' || userRole === 'Officer') && (
          <button
            className={`${styles.tab} ${activeTab === 'applications' ? styles.active : ''}`}
            onClick={() => {
              setActiveTab('applications');
              fetchApplications();
            }}
          >
            Applications
            {applicationCount > 0 && (
              <span className={styles.badge}>{applicationCount}</span>
            )}
          </button>
        )}
        {(userRole === 'Guild Master' || userRole === 'Officer') && (
          <button
            className={`${styles.tab} ${activeTab === 'management' ? styles.active : ''}`}
            onClick={() => setActiveTab('management')}
          >
            Management
          </button>
        )}
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'members' && renderMembers()}
        {activeTab === 'vault' && renderVault()}
        {activeTab === 'league' && renderLeague()}
        {activeTab === 'applications' && renderApplications()}
        {activeTab === 'management' && renderManagement()}
      </div>

      {selectedBuilding && guild && (
        <BuildingDetailModal
          building={selectedBuilding}
          guildLevel={guild.level}
          guildId={guild.id}
          userRole={userRole}
          onClose={() => setSelectedBuilding(null)}
          onUpdate={() => {
            fetchGuildData();
            fetchApplicationCount();
          }}
        />
      )}
      
      <Modal
        show={modalState.show}
        title={modalState.title}
        message={modalState.message}
        onClose={() => setModalState({ ...modalState, show: false })}
        onConfirm={modalState.type === 'confirm' ? modalState.onConfirm : undefined}
      />
    </div>
  );
};

export default GuildDashboard;
